/**
 * Sentinel error codes a store purchase-confirmation endpoint can return in its
 * `error` field, for cases where the caller has to BEHAVE differently rather
 * than just show a message.
 *
 * Which store failures land in which bucket, and why the default falls the way
 * it does, is in `design-docs/iap-subscriptions.md`, "Could not check is not
 * the same as not valid" (#4649).
 */

/**
 * The store could not be asked, so nothing is known about the purchase.
 *
 * A caller must NOT treat this as "the purchase is invalid". The purchase may
 * well have succeeded and been paid for — this code says only that our
 * confirmation attempt did not get an answer. Anything that reads it should
 * offer the buyer a way to try again rather than declaring a failure.
 *
 * Returned by `appleValidate`, `googlePlayValidate` and `coachTierValidate`.
 *
 * Its older sibling `'already_linked'` predates this file and is still written
 * as a bare string at each of its call sites; gathering it here would mean
 * touching all of them for no behaviour change.
 */
export const STORE_VALIDATE_UNAVAILABLE = 'store_unavailable';

/**
 * The store answered, and refused the request itself — Google Play's HTTP 400
 * for a purchase token it will not accept (#6239).
 *
 * Not "could not check": the store did answer, and its answer depends only on
 * what we sent, so asking again with the same token gets the same refusal for
 * ever. Not a "no such purchase" verdict either, which is why it has a code of
 * its own rather than the settled wording. A caller treats it as terminal: no
 * promise that the purchase will activate, and nothing parked to ask again.
 *
 * Apps released before this code existed fall through to their generic
 * purchase-failed message for it, which is the intended behaviour.
 */
export const STORE_VALIDATE_REQUEST_REFUSED = 'store_request_refused';

/**
 * Did this `error` code come back because the store could not be asked?
 *
 * The one place that question is answered, so that every client path deciding
 * what to tell a buyer asks it the same way. Each of those paths used to write
 * the comparison itself, and a later change to what counts as "could not check"
 * that missed one would silently go back to telling that store's buyers their
 * purchase failed — which is the whole defect #4651 fixed, and which was still
 * live in five other paths after it (see `design-docs/iap-subscriptions.md`,
 * "What the buyer is told").
 *
 * An absent code is NOT "could not check". A result with no error at all did
 * not fail, and a caller that lost the code somewhere in between has proved
 * nothing about the purchase — so the answer stays with the terminal wording,
 * which is what the code already did before this question was asked anywhere.
 */
export function isStoreCouldNotCheckCode(code: string | undefined): boolean {
  return code === STORE_VALIDATE_UNAVAILABLE;
}

/**
 * The store token this buyer sent is already linked to a DIFFERENT one of our
 * accounts, so this account may not claim it.
 *
 * Older than this file and still written as a bare string at the call sites that
 * RETURN it; what is gathered here is the question below, which several callers
 * have to ask. Not exported for that reason — nothing outside this file compares
 * against it, and the question is what callers want.
 */
const STORE_VALIDATE_ALREADY_LINKED = 'already_linked';

/**
 * What a store confirmation answers when the store's own signed record says a
 * different one of our accounts started the purchase — Apple's `appAccountToken`
 * or Google's `obfuscatedAccountId` (#5384). It began as the Google-only answer
 * and became store-neutral when Apple started refusing too; the TEXT is unchanged
 * on purpose, because apps already in people's hands compare against it.
 *
 * A sentence rather than a machine-readable code like `already_linked`, because
 * it also travels to the app as the buyer's error text; changing that would be a
 * change the people reading it can see. That is exactly why it is a constant and
 * not a literal at the call site: two places decide whether to keep asking about
 * an unconfirmed purchase by comparing against this text — `classifyGrantResult`
 * on the server and `isCoachTierSettledRefusalCode` below — and a reworded loose
 * literal would silently flip both to "keep asking for a fortnight".
 *
 * The server re-exports it as `STORE_ACCOUNT_MISMATCH_ERROR`.
 */
export const STORE_VALIDATE_ACCOUNT_MISMATCH =
  'Account ID mismatch. This purchase belongs to a different app account.';

/**
 * What a PERSONAL store confirmation (`appleValidate`, `googlePlayValidate`, and
 * the parked-claim heal that runs the same sequence) answers when the store
 * confirmed a real subscription and it is one of our coaching plans.
 *
 * The mirror image of the coach-tier rail's `not_a_coach_tier_product`. Without
 * it a coaching plan sent to the personal rail was recorded as personal Premium,
 * so one purchase sat in both `coachTierData` and the personal block
 * (df-bfd8dc723045). Like its mirror it is a fact about the product, so it
 * settles the question: asking again gets the same answer every time.
 *
 * The app has no special message for it. It falls through to the generic
 * "could not confirm" answer, which is right: our own app sends a coaching plan
 * to the coach-tier endpoint, so this only answers a bug or a foreign caller.
 */
export const STORE_VALIDATE_NOT_A_PERSONAL_PRODUCT = 'not_a_personal_product';

/**
 * Coach-tier confirmation refusals that SETTLE the question, so nothing is
 * gained by asking again.
 *
 * `not_a_coach_tier_product` — the store confirmed a real subscription and it is
 * not one of our coaching plans. `provider_mismatch` — our catalog says that
 * product belongs to the other store. Both are facts about the product.
 * `cross_provider_conflict` — the coach holds an active membership with a
 * different payment provider, which only the coach can resolve by managing that
 * membership; asking again cannot change it either.
 *
 * It lives in shared because BOTH sides have to agree on it. The server's parked
 * claim gives up on one of these rather than re-asking the store at every
 * sign-in (`helpers/pending-store-claim.ts`), and the phone's own record of an
 * unconfirmed purchase does exactly the same
 * (`client/src/services/coach-tier-pending-purchase.ts`). Two copies of that list
 * would let one side keep retrying a question the other has already given up on.
 */
export const COACH_TIER_SETTLED_REFUSAL_CODES: readonly string[] = [
  'not_a_coach_tier_product',
  'provider_mismatch',
  'cross_provider_conflict',
];

/**
 * Did the coach-tier confirmation settle the question, so that asking again is
 * pointless?
 *
 * Compared against the exported codes rather than by prefix or by message text,
 * so rewording a message cannot silently turn "give up" into "keep asking" or
 * the other way round.
 *
 * An absent code is NOT settled: a result with no error did not refuse anything,
 * and a caller that lost the code in between has proved nothing.
 */
export function isCoachTierSettledRefusalCode(code: string | undefined): boolean {
  if (!code) return false;
  if (code === STORE_VALIDATE_ALREADY_LINKED) return true;
  // the store's own account check: this purchase was made from a different app
  // account, and the store's signed record never changes on its own, so asking
  // again gets the same answer for ever. The server's `classifyGrantResult` gives a parked claim up
  // on it for that reason, and the phone has to agree — a side that keeps asking
  // about a question the other has closed is what one shared list prevents.
  if (code === STORE_VALIDATE_ACCOUNT_MISMATCH) return true;
  // the store refused the request itself (#6239), which it will do for that
  // token every time, so the phone stops asking just as the server does.
  if (code === STORE_VALIDATE_REQUEST_REFUSED) return true;
  return COACH_TIER_SETTLED_REFUSAL_CODES.includes(code);
}
